import React, { useCallback, useEffect, useMemo, useState } from "react";
import reportTemplatesService from "../../../services/reportTemplatesService";

/* ---------- helpers ---------- */

function extractFilename(cd, fallback) {
  if (!cd) return fallback;
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  return m ? decodeURIComponent(m[1].replace(/"/g, "")) : fallback;
}

function triggerBlobDownload(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function templateMeta(tpl) {
  const p = tpl?.payload || {};
  const vc =
    p.visibleColumns && typeof p.visibleColumns === "object"
      ? p.visibleColumns
      : {};
  const order =
    Array.isArray(p.columnOrder) && p.columnOrder.length
      ? p.columnOrder
      : Object.keys(vc);

  const enabledKeys = order.filter((k) => vc[k] !== false);
  const labels = p.columnLabels || {};

  /* Map view keys → human labels using reportColumnCatalog */
  let columnLabels = enabledKeys;
  try {
    // eslint-disable-next-line global-require
    const { TEMPLATE_COLUMNS } = require("./reportColumnCatalog");
    columnLabels = enabledKeys.map((vk) => {
      const def = TEMPLATE_COLUMNS.find((c) => c.viewKey === vk);
      return labels[vk] || def?.label || vk;
    });
  } catch {
    columnLabels = enabledKeys.map((vk) => labels[vk] || vk);
  }

  return {
    columnCount: enabledKeys.length,
    columnLabels,
    quickChipCount: Array.isArray(p.activeQuickChips)
      ? p.activeQuickChips.length
      : 0,
    sortCount: Array.isArray(p.sortState) ? p.sortState.length : 0,
    title: String(p.title || "").trim(),
    headerText: String(p.headerText || "").trim(),
    isDefault: !!tpl.is_default,
    updatedAt: tpl.updated_at || tpl.created_at || null,
  };
}

function formatRelativeDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86400000);
    if (days < 1) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

/* ============================================================ */
/* Component                                                     */
/* ============================================================ */

export default function ReportTemplatesPanel({
  onNew,
  onEdit,
  onView,
  refreshKey = 0,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [flash, setFlash] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // "recent" | "name" | "columns"

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await reportTemplatesService.list();
      setItems(list);
    } catch {
      setItems([]);
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!menuOpenId) return;
    const h = () => setMenuOpenId(null);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpenId]);

  const showFlash = (msg) => {
    setFlash(msg);
    if (showFlash._t) clearTimeout(showFlash._t);
    showFlash._t = setTimeout(() => setFlash(""), 2500);
  };

  /* ---------- Filtered + sorted list ---------- */

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items;
    if (q) {
      list = list.filter((t) => {
        const name = String(t.name || "").toLowerCase();
        const title = String(t.payload?.title || "").toLowerCase();
        return name.includes(q) || title.includes(q);
      });
    }

    const withMeta = list.map((t) => ({ tpl: t, meta: templateMeta(t) }));

    withMeta.sort((a, b) => {
      if (sortBy === "name") {
        return String(a.tpl.name || "").localeCompare(
          String(b.tpl.name || ""),
        );
      }
      if (sortBy === "columns") {
        return b.meta.columnCount - a.meta.columnCount;
      }
      /* recent */
      const aD = a.tpl.updated_at || a.tpl.created_at || "";
      const bD = b.tpl.updated_at || b.tpl.created_at || "";
      return String(bD).localeCompare(String(aD));
    });

    return withMeta;
  }, [items, search, sortBy]);

  /* ---------- Actions ---------- */

  const handleDownload = async (tpl, format) => {
    setMenuOpenId(null);
    setBusyId(tpl.id);
    try {
      const { blob, contentDisposition } =
        await reportTemplatesService.downloadSample(tpl.id, format);
      const fallback = `${(tpl.name || "template").replace(
        /\s+/g,
        "_",
      )}_sample.${format}`;
      const name = extractFilename(contentDisposition, fallback);
      triggerBlobDownload(blob, name);
      showFlash(`Downloaded "${name}"`);
    } catch {
      setError("Failed to download sample");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (tpl) => {
    if (
      !window.confirm(
        `Delete template "${tpl.name}"? This cannot be undone.`,
      )
    )
      return;
    try {
      await reportTemplatesService.remove(tpl.id);
      showFlash("Template deleted");
      await load();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to delete template";
      setError(msg);
    }
  };

  /* ---------- Render ---------- */

  return (
    <div className="r6-tpl-grid-page">
      {/* ==================== HEADER ==================== */}
      <div className="r6-tpl-grid-head">
        <div className="r6-tpl-grid-head-title">
          <h2 className="r6-tpl-grid-title">
            Report Templates
            {items.length > 0 && (
              <span className="r6-tpl-count-badge">{items.length}</span>
            )}
          </h2>
          <p className="r6-tpl-grid-sub">
            Reusable column and filter layouts for reports.
          </p>
        </div>

        <div className="r6-tpl-grid-actions">
          <div className="r6-tpl-search">
            <i className="fas fa-search" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates…"
              aria-label="Search templates"
            />
            {search && (
              <button
                type="button"
                className="r6-tpl-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          <select
            className="r6-tpl-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort templates"
          >
            <option value="recent">Recently updated</option>
            <option value="name">Name (A→Z)</option>
            <option value="columns">Most columns</option>
          </select>

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onNew}
          >
            <i className="fas fa-plus" /> New Template
          </button>
        </div>
      </div>

      {/* ==================== FLASH / ERROR ==================== */}
      {flash && (
        <div className="r6-tpl-flash">
          <i className="fas fa-check-circle" /> {flash}
        </div>
      )}
      {error && (
        <div className="error-message" role="alert">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {/* ==================== LIST ==================== */}
      {loading ? (
        <ul className="r6-tpl-grid">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="r6-tpl-tile r6-tpl-tile-skeleton" />
          ))}
        </ul>
      ) : filtered.length === 0 && !search ? (
        <div className="r6-tpl-empty-state">
          <div className="r6-tpl-empty-icon">
            <i className="fas fa-layer-group" />
          </div>
          <h3>No templates yet</h3>
          <p>
            Build a reusable layout for reports — columns, filters, and sort,
            saved once and reused everywhere.
          </p>
          <button type="button" className="btn btn-primary" onClick={onNew}>
            <i className="fas fa-plus" /> Create your first template
          </button>
        </div>
      ) : filtered.length === 0 && search ? (
        <div className="r6-tpl-empty-state">
          <div className="r6-tpl-empty-icon">
            <i className="fas fa-search" />
          </div>
          <h3>No matches</h3>
          <p>
            No template matches "<strong>{search}</strong>".
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSearch("")}
          >
            Clear search
          </button>
        </div>
      ) : (
        <ul className="r6-tpl-grid">
          {filtered.map(({ tpl, meta }) => (
            <TemplateTile
              key={tpl.id}
              tpl={tpl}
              meta={meta}
              busy={busyId === tpl.id}
              menuOpen={menuOpenId === tpl.id}
              onToggleMenu={(e) => {
                e.stopPropagation();
                setMenuOpenId((cur) => (cur === tpl.id ? null : tpl.id));
              }}
              onView={() => onView?.(tpl.id)}
              onEdit={() => onEdit?.(tpl.id)}
              onDownload={(fmt) => handleDownload(tpl, fmt)}
              onDelete={() => handleDelete(tpl)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================ */
/* Tile                                                          */
/* ============================================================ */

function TemplateTile({
  tpl,
  meta,
  busy,
  menuOpen,
  onToggleMenu,
  onView,
  onEdit,
  onDownload,
  onDelete,
}) {
  const topColumns = meta.columnLabels.slice(0, 3);
  const moreColumns = Math.max(0, meta.columnCount - 3);

  return (
    <li
      className="r6-tpl-tile"
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onView?.();
        }
      }}
    >
      {/* ---------- top: icon + name ---------- */}
      <div className="r6-tpl-tile-top">
        <div className="r6-tpl-tile-icon">
          <i className="fas fa-layer-group" />
        </div>
        <div className="r6-tpl-tile-name-wrap">
          <div className="r6-tpl-tile-name" title={tpl.name}>
            {tpl.name}
          </div>
          {meta.title && (
            <div className="r6-tpl-tile-subtitle" title={meta.title}>
              {meta.title}
            </div>
          )}
        </div>
        {meta.isDefault && (
          <span className="r6-tpl-tile-default" title="Default template">
            ★
          </span>
        )}
      </div>

      {/* ---------- middle: column chips ---------- */}
      <div className="r6-tpl-tile-chips">
        {topColumns.length === 0 ? (
          <span className="r6-tpl-tile-chips-empty">Default columns</span>
        ) : (
          <>
            {topColumns.map((lbl, i) => (
              <span key={i} className="r6-tpl-tile-chip">
                {lbl}
              </span>
            ))}
            {moreColumns > 0 && (
              <span className="r6-tpl-tile-chip r6-tpl-tile-chip-more">
                +{moreColumns}
              </span>
            )}
          </>
        )}
      </div>

      {/* ---------- bottom: meta + actions ---------- */}
      <div className="r6-tpl-tile-bottom">
        <div className="r6-tpl-tile-meta">
          <span title={`${meta.columnCount} columns`}>
            <i className="fas fa-table-columns" /> {meta.columnCount}
          </span>
          {meta.quickChipCount > 0 && (
            <span title={`${meta.quickChipCount} quick filter(s)`}>
              <i className="fas fa-bolt" /> {meta.quickChipCount}
            </span>
          )}
          {meta.sortCount > 0 && (
            <span title={`${meta.sortCount} sort rule(s)`}>
              <i className="fas fa-sort" /> {meta.sortCount}
            </span>
          )}
          <span className="r6-tpl-tile-meta-date">
            {formatRelativeDate(meta.updatedAt)}
          </span>
        </div>

        <div
          className="r6-tpl-tile-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="r6-tpl-icon-btn"
            title="Edit template"
            onClick={onEdit}
          >
            <i className="fas fa-pen" />
          </button>

          <div className="r6-tpl-dl-wrap">
            <button
              type="button"
              className="r6-tpl-icon-btn"
              title="Download sample"
              disabled={busy}
              onClick={onToggleMenu}
            >
              {busy ? (
                <i className="fas fa-spinner fa-spin" />
              ) : (
                <i className="fas fa-download" />
              )}
            </button>
            {menuOpen && (
              <div
                className="r6-tpl-dl-menu"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <button type="button" onClick={() => onDownload("xlsx")}>
                  <i className="fas fa-file-excel" /> Excel sample
                </button>
                <button type="button" onClick={() => onDownload("csv")}>
                  <i className="fas fa-file-csv" /> CSV sample
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="r6-tpl-icon-btn r6-tpl-icon-btn-danger"
            title="Delete template"
            onClick={onDelete}
          >
            <i className="fas fa-trash" />
          </button>
        </div>
      </div>
    </li>
  );
}